/**
 * Pure helpers that turn a flat Card[] into the 3-layer inventory hierarchy
 * (Identity → Version → Copy) the inventory list now renders against.
 *
 * Two callers should ever exist:
 *   - `/inventory/` list — uses `groupCards(filtered)` after the existing
 *     filter pipeline runs and renders the resulting NameGroup[].
 *   - tests / debugging — pure functions, no React.
 *
 * Why these specific keys, in plain English:
 *   - Identity is "the card people ask for at the counter" → `name` (lowercased).
 *     A Charizard from Base and a Charizard from Evolutions live under one
 *     L1 header so the user sees "I own 6 Charizards" first, then drills.
 *   - Version is "the exact printing" → `setCode + cardNumber + variant + finish`.
 *     Two NM and one LP of the *same* printing still belong to one Version
 *     (condition is a per-copy attribute, not part of identity).
 *   - Copy is "the physical stack on my shelf" → one Card record per L3 row.
 *     A stack with quantity > 1 still renders as one row labeled "×N";
 *     splitting a stack into individual copies is a separate ticket.
 */

import type { Card } from "@/context/AppContext";

export function identityKey(c: Card): string {
  return c.name.trim().toLowerCase();
}

export function versionKey(c: Card): string {
  // Prefer setCode (stable id) over setName (display string). Empty fields
  // collapse to "?" so cards with no set still cluster together instead of
  // each occupying their own undefined-set bucket.
  const set = (c.setCode || c.setName || "?").trim().toLowerCase();
  const num = (c.cardNumber || "?").trim().toLowerCase();
  const variant = (c.variant || "").trim().toLowerCase();
  const finish = (c.finish || "").trim().toLowerCase();
  return `${set}|${num}|${variant}|${finish}`;
}

export function versionLabel(c: Card): string {
  const variantFinish = [c.variant?.trim(), c.finish?.trim()]
    .filter(Boolean)
    .join(" ");
  const parts = [
    c.setName || c.setCode || null,
    c.cardNumber ? `#${c.cardNumber}` : null,
    variantFinish || null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Unknown version";
}

export interface GroupAggregate {
  totalQty: number;
  totalValue: number;
  statusBreakdown: {
    Listed: number;
    Unlisted: number;
    "In Package": number;
    Sold: number;
  };
  thumbnailRef: string | null;
}

export function aggregateGroup(cards: Card[]): GroupAggregate {
  const status: GroupAggregate["statusBreakdown"] = {
    Listed: 0,
    Unlisted: 0,
    "In Package": 0,
    Sold: 0,
  };
  let totalQty = 0;
  let totalValue = 0;
  let thumbnailRef: string | null = null;
  let thumbnailCreatedAt = "";
  for (const c of cards) {
    const qty = c.quantity ?? 0;
    totalQty += qty;
    totalValue += qty * (c.marketPrice ?? 0);
    status[c.listingStatus] = (status[c.listingStatus] ?? 0) + qty;
    // Pick the most-recently-added card's image as the group thumbnail so
    // re-imports / replacements naturally surface to the top.
    if (c.imageFrontRef && c.createdAt > thumbnailCreatedAt) {
      thumbnailRef = c.imageFrontRef;
      thumbnailCreatedAt = c.createdAt;
    }
  }
  return { totalQty, totalValue, statusBreakdown: status, thumbnailRef };
}

export interface VersionGroup {
  key: string;
  label: string;
  cards: Card[];
  aggregate: GroupAggregate;
}

export interface NameGroup {
  key: string;
  /** Display name — the first card's untouched `name` (preserves casing). */
  name: string;
  cards: Card[];
  aggregate: GroupAggregate;
  versions: VersionGroup[];
}

/**
 * Groups a flat (already-filtered) Card[] into the L1 → L2 nested shape.
 * Preserves the input order across keys — pass the existing sort upstream
 * (e.g. set-recency → cardNumber → name) and the grouped output keeps that
 * intent: groups appear in the order their first card appeared.
 */
export function groupCards(cards: Card[]): NameGroup[] {
  const byName = new Map<string, NameGroup>();
  for (const c of cards) {
    const nk = identityKey(c);
    let group = byName.get(nk);
    if (!group) {
      group = {
        key: nk,
        name: c.name,
        cards: [],
        aggregate: aggregateGroup([]),
        versions: [],
      };
      byName.set(nk, group);
    }
    group.cards.push(c);
  }
  // Second pass: nest versions inside each name group, recompute aggregates.
  for (const group of byName.values()) {
    const byVersion = new Map<string, VersionGroup>();
    for (const c of group.cards) {
      const vk = versionKey(c);
      let v = byVersion.get(vk);
      if (!v) {
        v = {
          key: vk,
          label: versionLabel(c),
          cards: [],
          aggregate: aggregateGroup([]),
        };
        byVersion.set(vk, v);
      }
      v.cards.push(c);
    }
    for (const v of byVersion.values()) {
      v.aggregate = aggregateGroup(v.cards);
    }
    group.versions = Array.from(byVersion.values());
    group.aggregate = aggregateGroup(group.cards);
  }
  return Array.from(byName.values());
}
